import { useDispatch, useSelector } from 'react-redux';
import { setDayMenuDate, setDayMenuOtherUserDate } from '../../Redux/store';
import DayButton from './DayButton';
import { getLocalTodayString } from '../../utils/dateFormat';

// priority: peak-test > shared-ov-actual > shared-ov-predicted > shared-ov > peak-predicted > predicted-ovulation+insemination > predicted-ovulation > predicted-period > insemination > period-start > period > event > normal
function getDayType(dayData) {
  if (!dayData) return 'normal';
  const { ipData, events, sharedOvulationPredictions } = dayData;
  if (ipData?.peak || ipData?.peakTest) return 'peak-test';

  // Check shared ovulations for actual vs predicted
  const hasSharedActual = sharedOvulationPredictions?.some(p => p.ovulationActual);
  const hasSharedPredicted = sharedOvulationPredictions?.some(p => p.ovulationPrediction);

  if (hasSharedActual && hasSharedPredicted) return 'shared-ov-both';
  if (hasSharedActual) return 'shared-ov-actual';
  if (hasSharedPredicted) return 'shared-ov-predicted';
  if (sharedOvulationPredictions?.length) return 'shared-ov';

  if (ipData?.peakPredicted) return 'peak-predicted';
  // Combined: both predicted ovulation and insemination day
  if (ipData?.predictedOvulation && ipData?.inseminationDay) return 'predicted-ovulation-insemination';
  if (ipData?.predictedOvulation) return 'predicted-ovulation';
  if (ipData?.predictedPeriod) return 'predicted-period';
  if (ipData?.inseminationDay) return 'insemination';
  if (ipData?.periodStart) return 'period-start';
  if (ipData?.periodDay) return 'period';
  if (events?.some((e) => !e.user)) return 'event';
  return 'normal';
}

// style classes per day type
const TYPE_CLASSES = {
  'peak-test': 'bg-purple-500 text-white font-semibold',
  'shared-ov-actual': 'bg-purple-300 text-white font-semibold',
  'shared-ov-predicted': 'border-2 border-purple-500',
  'shared-ov-both': 'bg-purple-200 border-2 border-purple-500 text-white font-semibold',
  'shared-ov': 'bg-primary/20 text-primary font-semibold',
  'peak-predicted': 'bg-primary/20 text-primary font-semibold',
  'predicted-ovulation': 'border-2 border-purple-500',
  'predicted-ovulation-insemination': 'border-2 border-purple-500',
  'predicted-period': 'border-2 border-destructive',
  insemination: 'text-blue-400',
  'period-start': 'period-start',
  period: 'period-day',
  event: 'ring-2 ring-primary',
};

// label text per day type (only some types show a label)
const TYPE_LABELS = {
  'peak-test': 'Peak',
  'shared-ov': 'Ovulation',
  'peak-predicted': 'Pred. Ov.',
};

function Day({ dateStr, dayNum, calendarData, userId = null, calendarId = null }) {
  const dispatch = useDispatch();
  const currentUserId = useSelector((s) => s.user.profile?.id);
  const dayData = calendarData?.[dateStr];
  const isToday = dateStr === getLocalTodayString();

  const type = getDayType(dayData);
  const sharedCount = dayData?.sharedOvulationPredictions?.length ?? 0;
  const eventCount = dayData?.events?.filter((e) => !e.user).length ?? 0;
  const cd = dayData?.ipData?.cd;
  const maxTestValue = dayData?.maxTestValue;

  // Calculate purple background opacity based on max test value
  const testOpacity = maxTestValue !== undefined && maxTestValue !== null
    ? Math.min(1, Math.max(0.05, maxTestValue / 2))
    : 0;

  let classes = 'flex h-full w-full flex-col items-center justify-center rounded-full text-sm';
  if (isToday) classes += ' ring-2 ring-[#bfdbfe] font-bold';
  classes += ` ${TYPE_CLASSES[type] ?? ''}`;

  // Apply purple background for any day with test values
  const hasTestValue = maxTestValue !== undefined && maxTestValue !== null;
  const backgroundStyle = hasTestValue
    ? { backgroundColor: `rgba(147, 51, 234, ${testOpacity})` }
    : undefined;

  const buttonId = `day-${dateStr}-${userId || currentUserId}`;

  const dataDate = dateStr;

  const handleDayClick = () => {
    if (userId === null || userId === currentUserId) {
      dispatch(setDayMenuDate(dateStr));
    } else {
      dispatch(setDayMenuOtherUserDate({ date: dateStr, userId }));
    }
  };

  return (
    <div className="relative aspect-square flex flex-col items-center justify-center">
      <DayButton
        type={type}
        dayNum={dayNum}
        buttonId={buttonId}
        dataDate={dataDate}
        calendarId={calendarId}
        classes={classes}
        backgroundStyle={backgroundStyle}
        onClick={handleDayClick}
        TYPE_LABELS={TYPE_LABELS}
        cd={cd}
        hasTestValue={hasTestValue}
        sharedCount={sharedCount}
        eventCount={eventCount}
        isToday={isToday}
      />
      {/* Max test value below day circle */}
      {hasTestValue && (
        <span className="text-[10px] font-semibold text-purple-500 leading-none mt-0.5">
          {maxTestValue.toFixed(2)}
        </span>
      )}
    </div>
  );
}

export default Day;
